if (!customElements.get('multi-buy-banner')) {
  customElements.define(
    'multi-buy-banner',
    class MultiBuyBanner extends HTMLElement {
      requestId = 0;

      connectedCallback() {
        this.productType = this.dataset.productType.trim().toLowerCase();
        this.targetQuantity = Number(this.dataset.targetQuantity) || 5;

        this.titleElement = this.querySelector('[data-title]');
        this.descriptionElement = this.querySelector('[data-description]');
        this.slotsElement = this.querySelector('[data-slots]');
        this.progressElement = this.querySelector('[data-progress]');
        this.completeIcon = this.querySelector('[data-complete-icon]');
        this.checkmark = this.completeIcon.querySelector('svg');

        this.cartUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate
        );

        document.addEventListener(
          'cart:recommendation-added',
          this.onRecommendationAdded
        );

        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();

        document.removeEventListener(
          'cart:recommendation-added',
          this.onRecommendationAdded
        );

        window.removeEventListener('pageshow', this.onPageShow);

        clearTimeout(this.refreshTimer);
        this.requestId += 1;
        this.requestController?.abort();
      }

      onCartUpdate = (event) => {
        const cart = event?.cartData;

        if (cart?.errors || cart?.status) return;

        this.scheduleRefresh(this.isFullCart(cart) ? cart : null);
      };

      onRecommendationAdded = () => this.scheduleRefresh();

      onPageShow = (event) => {
        if (event.persisted) this.scheduleRefresh();
      };

      isFullCart(cart) {
        return (
          Array.isArray(cart?.items) &&
          Number.isFinite(cart.item_count)
        );
      }

      scheduleRefresh(cart = null) {
        clearTimeout(this.refreshTimer);
        this.requestController?.abort();

        const requestId = ++this.requestId;

        this.refreshTimer = setTimeout(() => {
          this.refreshCart(cart, requestId);
        }, 50);
      }

      async refreshCart(cart, requestId) {
        if (!this.isConnected) return;

        try {
          if (!cart) {
            this.requestController = new AbortController();

            const root = window.Shopify?.routes?.root || '/';

            const response = await fetch(`${root}cart.js`, {
              headers: {
                Accept: 'application/json',
              },
              cache: 'no-store',
              signal: this.requestController.signal,
            });

            if (!response.ok) {
              throw new Error(`Cart request failed: ${response.status}`);
            }

            cart = await response.json();
          }

          if (!this.isConnected || requestId !== this.requestId) return;

          if (!this.isFullCart(cart)) {
            throw new Error('Invalid cart response');
          }

          this.renderCart(cart);
        } catch (error) {
          if (
            error.name !== 'AbortError' &&
            requestId === this.requestId
          ) {
            console.warn('[MultiBuyBanner] Unable to refresh:', error);
          }
        }
      }

      renderCart(cart) {
        const matchingItems = cart.items.filter((item) => {
          return (
            String(item.product_type || '').trim().toLowerCase() ===
            this.productType
          );
        });

        const quantity = matchingItems.reduce(
          (total, item) => total + item.quantity,
          0
        );

        const complete = quantity >= this.targetQuantity;
        const fragment = document.createDocumentFragment();

        let filledSlots = 0;

        for (const item of matchingItems) {
          const units = Math.min(
            item.quantity,
            this.targetQuantity - filledSlots
          );

          for (let unit = 0; unit < units; unit += 1) {
            fragment.append(this.createFilledSlot(item));
            filledSlots += 1;
          }

          if (filledSlots >= this.targetQuantity) break;
        }

                for (
          let index = filledSlots;
          index < this.targetQuantity;
          index += 1
        ) {
          const slot = this.createElement(
            'li',
            'multi-buy-banner__slot is-empty'
          );

          const plus = this.createElement(
            'span',
            'multi-buy-banner__slot-plus',
            '+'
          );

          plus.setAttribute('aria-hidden', 'true');

          let content = slot;
          let label = `${this.productType} — ${index + 1}`;

          if (this.dataset.emptySlotLink) {
            content = this.createElement(
              'a',
              'multi-buy-banner__empty-link'
            );

            content.href = this.dataset.emptySlotLink;
            label = this.dataset.emptySlotLabel || 'Browse products';

            slot.append(content);
          }

          content.append(
            plus,
            this.createElement('span', 'visually-hidden', label)
          );

          fragment.append(slot);
        }

        this.slotsElement.replaceChildren(fragment);
        this.classList.toggle('is-complete', complete);
        this.completeIcon.hidden = !complete;
        this.dataset.qualifyingQuantity = String(quantity);

        const title = complete
          ? this.dataset.completeTitle
          : this.dataset.initialTitle;

        const description = complete
          ? this.dataset.completeDescription
          : this.dataset.initialDescription;

        this.setText(this.titleElement, title);
        this.setText(this.descriptionElement, description, true);

        const progress =
          `${this.productType}: ${filledSlots} / ${this.targetQuantity}` +
          (complete && title ? `. ${title}` : '');

        if (this.progressElement.textContent.trim() !== progress) {
          this.progressElement.textContent = progress;
        }
      }

      createFilledSlot(item) {
        const slot = this.createElement(
          'li',
          'multi-buy-banner__slot is-filled'
        );

        const imageUrl = item.featured_image?.url || item.image;
        const title = item.product_title || item.title || '';

        if (imageUrl) {
          const image = this.createElement(
            'img',
            'multi-buy-banner__image'
          );

          const url = new URL(imageUrl, window.location.origin);
          url.searchParams.set('width', '240');

          image.src = url.toString();
          image.alt = title;
          image.loading = 'lazy';
          image.decoding = 'async';
          image.width = item.featured_image?.width || 240;
          image.height = item.featured_image?.height || 320;

          slot.append(image);
        } else {
          slot.append(
            this.createElement(
              'span',
              'multi-buy-banner__image-placeholder',
              title
            )
          );
        }

        // Every filled slot gets a checkmark, even below the target.
        const check = this.createElement(
          'span',
          'multi-buy-banner__slot-check'
        );

        check.setAttribute('data-slot-check', '');
        check.append(this.checkmark.cloneNode(true));

        slot.append(check);

        return slot;
      }

      createElement(tag, className, text) {
        const element = document.createElement(tag);

        element.className = className;

        if (text !== undefined) {
          element.textContent = text;
        }

        return element;
      }

      setText(element, value = '', multiline = false) {
        element.hidden = !value.trim();
        element.replaceChildren();

        const lines = multiline ? value.split(/\r?\n/) : [value];

        lines.forEach((line, index) => {
          if (index) {
            element.append(document.createElement('br'));
          }

          element.append(document.createTextNode(line));
        });
      }
    }
  );
}